import cx from 'classnames';
import Image from 'next/image';
import { forwardRef, MouseEvent } from 'react';

import { IconChevronRight } from '../../../../design-system/components/icons/ChevronRight';
import { IconPin } from '../../../../design-system/components/icons/Pin';
import { RATIO } from '../../../../design-system/components/ratio/Ratio';
import { Typography } from '../../../../design-system/components/typography/Typography';
import { SearchSuggestion } from '../../../../resources/searchSuggestions/domain/types/searchSuggestions';
import { PickDomain } from '../../../../shared/utilities/types/PickDomain';
import { imageLoaderForPicture } from '../../../Layout/Image/URL/ImageURL';
import { defaultPicture } from '../../../Picture/Picture';

import style from './SearchResultItem.module.css';

type Props = {
    suggestion: PickDomain<
        SearchSuggestion,
        'type' | 'name' | 'coverPicture',
        {
            parentName: SearchSuggestion['parentName'] | null;
        }
    >;
    href: string;
    isActive: boolean;
    isSubRegion: boolean;
    isPending: boolean;
    isDisabled: boolean;
    meta: string | null;
    id?: string;
    onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
    onMouseEnter?: () => void;
};

export const SearchResultItem = forwardRef<HTMLAnchorElement, Props>(function SearchResultItem(
    {
        suggestion,
        href,
        isActive,
        isSubRegion,
        isPending,
        isDisabled,
        meta,
        id,
        onClick,
        onMouseEnter,
    },
    ref,
) {
    const thumbnailSize = isSubRegion ? 40 : 56;
    const showParent =
        !isSubRegion && suggestion.type !== 'destination' && suggestion.parentName !== null;
    const showPinIcon =
        suggestion.type === 'place' || suggestion.coverPicture.id === defaultPicture().id;

    return (
        <a
            ref={ref}
            id={id}
            href={href}
            role="option"
            aria-selected={isActive}
            aria-disabled={isDisabled || undefined}
            className={cx(isSubRegion ? style.itemSubRegion : style.item, {
                [style.itemActive]: isActive,
            })}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
        >
            <div className={isSubRegion ? style.thumbnailSmall : style.thumbnail}>
                {showPinIcon ? (
                    <IconPin className={style.thumbnailIcon} />
                ) : (
                    <Image
                        className={style.thumbnailImg}
                        src={suggestion.coverPicture.id}
                        alt=""
                        width={thumbnailSize}
                        height={thumbnailSize}
                        loader={imageLoaderForPicture(suggestion.coverPicture, {
                            aspectRatio: RATIO['1/1'],
                        })}
                    />
                )}
            </div>
            <div className={style.content}>
                <span className={style.titleRow}>
                    <Typography scale="primary-xs-bold" className={style.title}>
                        {suggestion.name}
                    </Typography>
                    {showParent ? (
                        <Typography
                            scale="primary-xs-bold"
                            className={style.parent}
                        >{`(${suggestion.parentName})`}</Typography>
                    ) : null}
                </span>
                {meta ? (
                    <Typography scale="primary-xxs-regular" className={style.meta}>
                        {meta}
                    </Typography>
                ) : null}
            </div>
            {isPending ? (
                <span className={style.spinner} aria-hidden="true" data-testid="pending-spinner" />
            ) : (
                <IconChevronRight className={style.chevron} />
            )}
        </a>
    );
});
